import { useCallback, useEffect, useMemo, useState } from "react";
import { socket } from "../lib/socket";
import { useAuth } from "./useAuth";
import type {
  AckResponse,
  FriendRequestSummary,
  FriendSummary,
  GameInvite,
  InviteResolution,
  JoinData,
  PresenceStatus,
  ProtocolError,
  PublicProfile,
  SocialSnapshot,
} from "../types/protocol";

const ACK_TIMEOUT = 9_000;
const MIN_SEARCH_LENGTH = 2;

const timeoutError: ProtocolError = {
  code: "ACK_TIMEOUT",
  message: "Le serveur met trop de temps à répondre. Réessaie dans un instant.",
};

const unauthenticatedError: ProtocolError = {
  code: "UNAUTHENTICATED",
  message: "Connecte-toi pour retrouver tes amis.",
};

const presenceRank: Record<PresenceStatus, number> = {
  online: 0,
  "in-lobby": 1,
  "in-game": 2,
  offline: 3,
};

/** Les amis disponibles remontent : c'est avec eux qu'on peut jouer tout de suite. */
export const sortFriends = (friends: readonly FriendSummary[]): FriendSummary[] =>
  [...friends].sort((first, second) => {
    const byPresence = presenceRank[first.status] - presenceRank[second.status];
    if (byPresence !== 0) return byPresence;
    return first.displayName.localeCompare(second.displayName, "fr", { sensitivity: "base" });
  });

/** Renvoie la même référence quand rien n'a expiré, pour éviter un rendu inutile. */
export const activeInvites = (invites: GameInvite[], now: number): GameInvite[] => {
  const kept = invites.filter((invite) => Date.parse(invite.expiresAt) > now);
  return kept.length === invites.length ? invites : kept;
};

/** Délai en millisecondes avant la prochaine expiration, `null` s'il n'y en a aucune. */
export const nextInviteExpiry = (invites: readonly GameInvite[], now: number): number | null => {
  let soonest: number | null = null;
  for (const invite of invites) {
    const deadline = Date.parse(invite.expiresAt);
    if (Number.isNaN(deadline)) continue;
    if (soonest === null || deadline < soonest) soonest = deadline;
  }
  return soonest === null ? null : soonest - now;
};

/**
 * `invite:received` et `social:state` peuvent se croiser : on garde l'union des
 * deux sources plutôt que de laisser un instantané en retard effacer une
 * invitation qui vient d'arriver.
 */
export const mergeInvites = (
  previous: GameInvite[],
  incoming: readonly GameInvite[],
  now: number,
): GameInvite[] => {
  const merged = new Map<string, GameInvite>();
  for (const invite of previous) merged.set(invite.id, invite);
  for (const invite of incoming) merged.set(invite.id, invite);
  const next = activeInvites([...merged.values()], now);
  const unchanged =
    next.length === previous.length && next.every((invite, index) => invite === previous[index]);
  return unchanged ? previous : next;
};

const socialMessages: Record<string, string> = {
  USER_NOT_FOUND: "Aucun joueur ne correspond à cet identifiant.",
  ALREADY_FRIENDS: "Vous êtes déjà amis.",
  REQUEST_ALREADY_PENDING: "Une demande est déjà en attente avec ce joueur.",
  REQUEST_NOT_FOUND: "Cette demande n’existe plus.",
  CANNOT_FRIEND_SELF: "Tu ne peux pas t’ajouter toi-même en ami.",
  FRIEND_LIMIT_REACHED: "Ta liste d’amis est pleine.",
  NOT_FRIENDS: "Vous n’êtes plus amis.",
  FRIEND_OFFLINE: "Cet ami n’est plus en ligne.",
  INVITE_NOT_FOUND: "Cette invitation n’est plus valable.",
  INVITE_EXPIRED: "Cette invitation a expiré.",
  INVITE_LIMIT_REACHED: "Trop d’invitations en cours. Patiente un instant.",
  ALREADY_INVITED: "Ce joueur a déjà reçu ton invitation.",
  UNAUTHENTICATED: "Connecte-toi pour retrouver tes amis.",
  ACCOUNTS_DISABLED: "Les comptes sont désactivés sur ce serveur.",
  NOT_IN_ROOM: "Crée ou rejoins une table avant d’inviter.",
  ALREADY_IN_ROOM: "Quitte ta table actuelle avant d’en rejoindre une autre.",
  INVALID_ROOM_STATE: "La partie a déjà commencé sur cette table.",
  NOT_HOST: "Seul l’hôte de la table peut inviter des amis.",
  ROOM_FULL: "La table est complète.",
  ROOM_NOT_FOUND: "Cette table n’existe plus.",
  GAME_ALREADY_STARTED: "La partie a déjà commencé.",
  RATE_LIMITED: "Trop de tentatives. Patiente un instant avant de réessayer.",
};

export const socialErrorMessage = (error: ProtocolError | null): string | null =>
  error === null ? null : socialMessages[error.code] ?? error.message;

export interface SocialClient {
  snapshot: SocialSnapshot | null;
  friends: FriendSummary[];
  incoming: FriendRequestSummary[];
  outgoing: FriendRequestSummary[];
  invites: GameInvite[];
  /** Amis déjà invités à la table courante : leur bouton attend une résolution. */
  invitedUserIds: string[];
  pending: string | null;
  error: ProtocolError | null;
  search: (query: string) => Promise<PublicProfile[]>;
  requestFriend: (handle: string) => Promise<boolean>;
  respondToRequest: (requestId: string, accept: boolean) => Promise<boolean>;
  cancelRequest: (requestId: string) => Promise<boolean>;
  removeFriend: (userId: string) => Promise<boolean>;
  sendInvite: (userId: string) => Promise<boolean>;
  respondToInvite: (inviteId: string, accept: boolean) => Promise<JoinData | null>;
  /** Quitter la table annule les invitations : plus rien n'est en attente. */
  resetInvited: () => void;
  dismissError: () => void;
}

export const useSocial = (): SocialClient => {
  const { status } = useAuth();
  const authenticated = status === "authenticated";
  const [snapshot, setSnapshot] = useState<SocialSnapshot | null>(null);
  const [invites, setInvites] = useState<GameInvite[]>([]);
  const [invitedUserIds, setInvitedUserIds] = useState<string[]>([]);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<ProtocolError | null>(null);

  useEffect(() => {
    if (!authenticated) {
      setSnapshot(null);
      setInvites([]);
      setInvitedUserIds([]);
      return;
    }

    const applySnapshot = (next: SocialSnapshot) => {
      setSnapshot(next);
      setInvites((current) => mergeInvites(current, next.invites, Date.now()));
    };
    // La reconnexion rejoue la présence de tout le monde : on repart de zéro.
    const sync = () => {
      socket.emit("social:sync", (response) => {
        if (response.ok) applySnapshot(response.data);
      });
    };
    const onInvite = (invite: GameInvite) => {
      setInvites((current) => mergeInvites(current, [invite], Date.now()));
    };
    const onResolved = ({ inviteId, profile }: InviteResolution) => {
      setInvites((current) => current.filter((invite) => invite.id !== inviteId));
      // Le sort de l'invitation est connu : cet ami redevient invitable.
      setInvitedUserIds((current) => current.filter((userId) => userId !== profile.userId));
    };

    socket.on("social:state", applySnapshot);
    socket.on("invite:received", onInvite);
    socket.on("invite:resolved", onResolved);
    socket.on("connect", sync);
    if (socket.connected) sync();

    return () => {
      socket.off("social:state", applySnapshot);
      socket.off("invite:received", onInvite);
      socket.off("invite:resolved", onResolved);
      socket.off("connect", sync);
    };
  }, [authenticated]);

  useEffect(() => {
    const delay = nextInviteExpiry(invites, Date.now());
    if (delay === null) return;
    const timer = window.setTimeout(
      () => setInvites((current) => activeInvites(current, Date.now())),
      Math.max(200, delay),
    );
    return () => window.clearTimeout(timer);
  }, [invites]);

  const command = useCallback(
    <T,>(
      key: string,
      emit: (ack: (response: AckResponse<T>) => void) => void,
    ): Promise<AckResponse<T>> => {
      if (!authenticated) {
        setError(unauthenticatedError);
        return Promise.resolve({ ok: false, error: unauthenticatedError });
      }
      setPending(key);
      setError(null);
      return new Promise((resolve) => {
        let settled = false;
        const finish = (response: AckResponse<T>) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          setPending((current) => (current === key ? null : current));
          if (!response.ok) setError(response.error);
          resolve(response);
        };
        const timer = window.setTimeout(() => finish({ ok: false, error: timeoutError }), ACK_TIMEOUT);
        emit(finish);
      });
    },
    [authenticated],
  );

  const search = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      if (trimmed.length < MIN_SEARCH_LENGTH) return [];
      const response = await command<PublicProfile[]>("search", (ack) =>
        socket.emit("friends:search", { query: trimmed }, ack),
      );
      return response.ok ? response.data : [];
    },
    [command],
  );

  const requestFriend = useCallback(
    async (handle: string) =>
      (await command("request", (ack) => socket.emit("friends:request", { handle: handle.trim() }, ack)))
        .ok,
    [command],
  );

  const respondToRequest = useCallback(
    async (requestId: string, accept: boolean) =>
      (
        await command(`respond:${requestId}`, (ack) =>
          socket.emit("friends:respond", { requestId, accept }, ack),
        )
      ).ok,
    [command],
  );

  const cancelRequest = useCallback(
    async (requestId: string) =>
      (await command(`cancel:${requestId}`, (ack) => socket.emit("friends:cancel", { requestId }, ack)))
        .ok,
    [command],
  );

  const removeFriend = useCallback(
    async (userId: string) =>
      (await command(`remove:${userId}`, (ack) => socket.emit("friends:remove", { userId }, ack))).ok,
    [command],
  );

  const sendInvite = useCallback(
    async (userId: string) => {
      const { ok } = await command(`invite:${userId}`, (ack) =>
        socket.emit("invite:send", { userId }, ack),
      );
      if (ok) {
        setInvitedUserIds((current) => (current.includes(userId) ? current : [...current, userId]));
      }
      return ok;
    },
    [command],
  );

  const respondToInvite = useCallback(
    async (inviteId: string, accept: boolean) => {
      const response = await command<JoinData | null>(`invite-respond:${inviteId}`, (ack) =>
        socket.emit("invite:respond", { inviteId, accept }, ack),
      );
      if (!response.ok) return null;
      setInvites((current) => current.filter((invite) => invite.id !== inviteId));
      return response.data;
    },
    [command],
  );

  const resetInvited = useCallback(
    () => setInvitedUserIds((current) => (current.length === 0 ? current : [])),
    [],
  );

  const dismissError = useCallback(() => setError(null), []);

  const friends = useMemo(() => sortFriends(snapshot?.friends ?? []), [snapshot]);
  const incoming = useMemo(
    () => (snapshot?.requests ?? []).filter((request) => request.direction === "incoming"),
    [snapshot],
  );
  const outgoing = useMemo(
    () => (snapshot?.requests ?? []).filter((request) => request.direction === "outgoing"),
    [snapshot],
  );

  return useMemo(
    () => ({
      snapshot,
      friends,
      incoming,
      outgoing,
      invites,
      invitedUserIds,
      pending,
      error,
      search,
      requestFriend,
      respondToRequest,
      cancelRequest,
      removeFriend,
      sendInvite,
      respondToInvite,
      resetInvited,
      dismissError,
    }),
    [
      cancelRequest,
      dismissError,
      error,
      friends,
      incoming,
      invitedUserIds,
      invites,
      outgoing,
      pending,
      removeFriend,
      requestFriend,
      resetInvited,
      respondToInvite,
      respondToRequest,
      search,
      sendInvite,
      snapshot,
    ],
  );
};
